// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the cryptography of own data (docs/AYNI_DATA_INTAKE_PLAN.md,
 * "Encryption and key management"; the byte formats are fixed in
 * docs/AYNI_DATA_INTAKE_API.md so the intake server can implement them).
 * Plain WebCrypto: the browser, Pages Functions, the delivery Worker and
 * the tests all run it. No DOM.
 *
 * - **Envelope `AYD1`** for every byte we encrypt with a dataset key
 *   (uploaded parts, exact values): `"AYD1"`, one kind byte, a random
 *   96-bit nonce, then AES-256-GCM ciphertext with its 16-byte tag. The
 *   additional data names where the bytes belong (tenant, dataset and the
 *   part or object path), so a part or chunk moved elsewhere fails to open.
 * - **Tenant wrapping key**: HKDF-SHA-256 of a root secret (one per key
 *   version) with the tenant's id — see `deriveTenantKek` for why.
 * - **Wrapped dataset key**: the 256-bit dataset key sealed with the
 *   tenant key by AES-256-GCM, its additional data naming tenant, dataset
 *   and key version, so a wrapped key copied onto another dataset's row
 *   does not unwrap.
 */

// ------------------------------------------------------------------ bytes

export function toBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function fromBase64Url(value: string): Uint8Array<ArrayBuffer> | null {
  if (!/^[A-Za-z0-9_-]*$/.test(value)) return null
  try {
    const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'))
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
    return out
  } catch {
    return null
  }
}

const utf8 = (s: string): Uint8Array<ArrayBuffer> => new TextEncoder().encode(s) as Uint8Array<ArrayBuffer>

export function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** SHA-256, lower-case hex, of bytes or of a string's UTF-8. */
export async function sha256Hex(data: Uint8Array | string): Promise<string> {
  const bytes = typeof data === 'string' ? utf8(data) : data
  return hex(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>)))
}

export const SHA256_HEX = /^[0-9a-f]{64}$/

/** Equal strings, in time that does not depend on where they differ. */
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  return diff === 0
}

// --------------------------------------------------------------- envelope

export const ENVELOPE_MAGIC = 'AYD1'
/** Envelope kinds: an uploaded original's part, or an exact-value object (Zarr). */
export const ENVELOPE_KIND = { part: 1, exact: 2 } as const
export type EnvelopeKind = (typeof ENVELOPE_KIND)[keyof typeof ENVELOPE_KIND]
const HEADER_BYTES = 4 + 1 + 12
const TAG_BYTES = 16
/** Bytes an envelope adds to its plaintext. */
export const ENVELOPE_OVERHEAD = HEADER_BYTES + TAG_BYTES

/** Additional data of an uploaded part: part `n` (1-based) of `total`. */
export const partAad = (tenant: string, dataset: string, job: string, n: number, total: number): string =>
  `ayni-data/v1|${ENVELOPE_KIND.part}|${tenant}|${dataset}|job/${job}/part/${n}/${total}`

/** Additional data of an exact-value object: its key relative to the dataset's prefix (`exact/<release>.zarr/...`). */
export const exactAad = (tenant: string, dataset: string, relativeKey: string): string =>
  `ayni-data/v1|${ENVELOPE_KIND.exact}|${tenant}|${dataset}|${relativeKey}`

async function aesKey(raw: Uint8Array, usages: Array<'encrypt' | 'decrypt'>): Promise<CryptoKey> {
  if (raw.length !== 32) throw new Error('A dataset key is 32 bytes')
  return crypto.subtle.importKey('raw', raw as Uint8Array<ArrayBuffer>, { name: 'AES-GCM' }, false, usages)
}

/** Seal `plaintext` with a dataset key into an `AYD1` envelope. */
export async function sealEnvelope(datasetKey: Uint8Array, kind: EnvelopeKind, aad: string, plaintext: Uint8Array): Promise<Uint8Array> {
  const nonce = crypto.getRandomValues(new Uint8Array(12))
  const key = await aesKey(datasetKey, ['encrypt'])
  const sealed = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: utf8(aad), tagLength: 128 }, key, plaintext as Uint8Array<ArrayBuffer>))
  const out = new Uint8Array(HEADER_BYTES + sealed.length)
  out.set(utf8(ENVELOPE_MAGIC), 0)
  out[4] = kind
  out.set(nonce, 5)
  out.set(sealed, HEADER_BYTES)
  return out
}

/** Open an `AYD1` envelope of `kind` with a dataset key, or null when it is not one, or was moved, changed or sealed with another key. */
export async function openEnvelope(datasetKey: Uint8Array | CryptoKey, kind: EnvelopeKind, aad: string, envelope: Uint8Array): Promise<Uint8Array | null> {
  if (envelope.length < ENVELOPE_OVERHEAD) return null
  if (String.fromCharCode(envelope[0], envelope[1], envelope[2], envelope[3]) !== ENVELOPE_MAGIC || envelope[4] !== kind) return null
  try {
    const key = datasetKey instanceof Uint8Array ? await aesKey(datasetKey, ['decrypt']) : datasetKey
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: envelope.slice(5, HEADER_BYTES), additionalData: utf8(aad), tagLength: 128 },
      key,
      envelope.slice(HEADER_BYTES),
    )
    return new Uint8Array(plain)
  } catch {
    return null
  }
}

/** A dataset key as a decrypt-only CryptoKey (the delivery Worker keeps this, not the bytes). */
export const datasetDecryptKey = (raw: Uint8Array): Promise<CryptoKey> => aesKey(raw, ['decrypt'])

// ------------------------------------------------------------------- keys

/** A new 256-bit dataset key. */
export function newDatasetKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32))
}

/** A root secret (a `DATA_KEK_V<n>` secret: 32 random bytes, base64url) as bytes, or null when it is not one. */
export function parseRootSecret(value: string | undefined): Uint8Array | null {
  const bytes = value ? fromBase64Url(value.trim()) : null
  return bytes && bytes.length >= 32 ? bytes : null
}

const KEK_SALT = 'ayni-data/tenant-kek/v1'

/**
 * The tenant's wrapping key for one key version: HKDF-SHA-256 (RFC 5869)
 * of that version's root secret, salt `ayni-data/tenant-kek/v1`, info
 * `tenant:<tenant id>`.
 *
 * Why one root secret per version and HKDF per tenant, not a secret per
 * tenant (decision O7: tenant keys live as Cloudflare secrets): a secret
 * can only be written through Cloudflare's API with an account token, so
 * a secret per tenant would mean the site holding a token that can rewrite
 * its own secrets, or a person adding one for every new customer; and
 * Pages and Workers cap the number of secrets. HKDF gives every tenant an
 * independent key (knowing one tenant's key says nothing about another's)
 * from one secret that a person sets once a year. Yearly rotation adds
 * `DATA_KEK_V<n+1>` and re-wraps every dataset key (`rewrap` in
 * functions/api/data/_keys.ts); an external key service later replaces
 * this function only.
 */
export async function deriveTenantKek(root: Uint8Array, tenant: string): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', root as Uint8Array<ArrayBuffer>, 'HKDF', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: utf8(KEK_SALT), info: utf8(`tenant:${tenant}`) },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

const wrapAad = (tenant: string, dataset: string, version: number): string => `ayni-data/dk/v1|${tenant}|${dataset}|${version}`

/** A dataset key sealed by its tenant key: `<nonce>.<ciphertext>`, base64url. */
export async function wrapKey(kek: CryptoKey, dk: Uint8Array, at: { tenant: string; dataset: string; version: number }): Promise<string> {
  const nonce = crypto.getRandomValues(new Uint8Array(12))
  const sealed = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: utf8(wrapAad(at.tenant, at.dataset, at.version)) }, kek, dk as Uint8Array<ArrayBuffer>)
  return `${toBase64Url(nonce)}.${toBase64Url(new Uint8Array(sealed))}`
}

/** The dataset key inside a wrapped key, or null when it does not open with this tenant key for this dataset and version. */
export async function unwrapKey(kek: CryptoKey, wrapped: string, at: { tenant: string; dataset: string; version: number }): Promise<Uint8Array | null> {
  const [n, c, extra] = wrapped.split('.')
  const nonce = n ? fromBase64Url(n) : null
  const sealed = c ? fromBase64Url(c) : null
  if (extra !== undefined || !nonce || nonce.length !== 12 || !sealed) return null
  try {
    const dk = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce, additionalData: utf8(wrapAad(at.tenant, at.dataset, at.version)) }, kek, sealed))
    return dk.length === 32 ? dk : null
  } catch {
    return null
  }
}

const sourceAad = (tenant: string, source: string, version: number): string => `ayni-data/src/v1|${tenant}|${source}|${version}`

/**
 * A continuous source's credentials sealed by its tenant key (the plan's
 * "Customer bucket keys: the XR server, the tenant key"):
 * `<nonce>.<ciphertext>`, base64url, bound to the tenant, the source and
 * the key version, so a sealed value moved to another source does not open.
 */
export async function sealSourceSecret(kek: CryptoKey, plaintext: string, at: { tenant: string; source: string; version: number }): Promise<string> {
  const nonce = crypto.getRandomValues(new Uint8Array(12))
  const sealed = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce, additionalData: utf8(sourceAad(at.tenant, at.source, at.version)) }, kek, utf8(plaintext))
  return `${toBase64Url(nonce)}.${toBase64Url(new Uint8Array(sealed))}`
}

/** The text inside a sealed source secret, or null when it does not open for this tenant, source and version. */
export async function openSourceSecret(kek: CryptoKey, sealedText: string, at: { tenant: string; source: string; version: number }): Promise<string | null> {
  const [n, c, extra] = sealedText.split('.')
  const nonce = n ? fromBase64Url(n) : null
  const sealed = c ? fromBase64Url(c) : null
  if (extra !== undefined || !nonce || nonce.length !== 12 || !sealed) return null
  try {
    return new TextDecoder().decode(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce, additionalData: utf8(sourceAad(at.tenant, at.source, at.version)) }, kek, sealed))
  } catch {
    return null
  }
}

// ---------------------------------------------------------------- Ed25519

const ED25519 = { name: 'Ed25519' }

/** An Ed25519 signing key held as a secret: JSON `{ "kid", "d", "x" }` (JWK fields, base64url). */
export interface SigningKey {
  kid: string
  key: CryptoKey
}

export async function importSigningKey(secret: string | undefined): Promise<SigningKey | null> {
  if (!secret) return null
  try {
    const j = JSON.parse(secret) as { kid?: unknown; d?: unknown; x?: unknown }
    if (typeof j.kid !== 'string' || !/^[A-Za-z0-9_-]{1,40}$/.test(j.kid) || typeof j.d !== 'string' || typeof j.x !== 'string') return null
    const key = await crypto.subtle.importKey('jwk', { kty: 'OKP', crv: 'Ed25519', d: j.d, x: j.x }, ED25519, false, ['sign'])
    return { kid: j.kid, key }
  } catch {
    return null
  }
}

/** Public keys a checker trusts: JSON `{ "<kid>": "<x>" }`; malformed is none. */
export function parsePublicKeys(value: string | undefined): Readonly<Record<string, string>> {
  if (!value?.trim()) return {}
  try {
    const parsed = JSON.parse(value) as unknown
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && Object.values(parsed).every((v) => typeof v === 'string')) {
      return parsed as Record<string, string>
    }
  } catch {
    // none
  }
  return {}
}

export async function signText(key: SigningKey, text: string): Promise<string> {
  return toBase64Url(new Uint8Array(await crypto.subtle.sign(ED25519, key.key, utf8(text))))
}

/** Whether `signature` (base64url) is `kid`'s signature of `text`, `kid` being one of `keys`. */
export async function verifyText(keys: Readonly<Record<string, string>>, kid: string, text: string, signature: string): Promise<boolean> {
  const x = Object.prototype.hasOwnProperty.call(keys, kid) ? keys[kid] : undefined
  const sig = fromBase64Url(signature)
  if (!x || !sig) return false
  try {
    const key = await crypto.subtle.importKey('jwk', { kty: 'OKP', crv: 'Ed25519', x }, ED25519, false, ['verify'])
    return await crypto.subtle.verify(ED25519, key, sig, utf8(text))
  } catch {
    return false
  }
}

/** A fresh Ed25519 key pair in the secret / public-key formats above (tests, and the owner's set-up script). */
export async function generateSigningKeyPair(kid: string): Promise<{ secret: string; publicKeys: string }> {
  const pair = (await crypto.subtle.generateKey(ED25519, true, ['sign', 'verify'])) as CryptoKeyPair
  const jwk = (await crypto.subtle.exportKey('jwk', pair.privateKey)) as JsonWebKey
  return { secret: JSON.stringify({ kid, d: jwk.d, x: jwk.x }), publicKeys: JSON.stringify({ [kid]: jwk.x }) }
}
